// TODO: Refactor file into smaller components. Will complete with - https://dev.azure.com/FirstAmCorp/AgentNetPortal/_workitems/edit/2268573/
import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { AppBar, AppHeader, BaseLink, CategoryType, NavigationLink, TopNav } from '@fluentsms/agentnet-web-components';
import { AgentNetServices, Comprehensive, Discownts, Knowledge } from '@fluentsms/agentnet-web-icons';
import { Button, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { getAgentNetKnowledgeUrl, getAgentNetMarketingUrl, getAnteUrl } from 'api/host-by-env';
import { getFirmDetails } from 'api/onBehalfOf/api';
import { getDefaultFirm } from 'api/profile/profile-utilities';
import { Firm } from 'api/profile/types';
import { AgentNetMarketing, AgentNetTitleEscrow, SoftwareIntegrationHub } from 'core/navbar/AppSwitcherIcons';
import { RoleId } from 'utilities/constants/role-ids';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import FileCreateContainer from 'features/files/file-create/FileCreateContainer';
import OrderCreateContainer from 'features/orderManagement/OrderCreateContainer';
import { FileCreateProvider } from 'hooks/FileCreateContext';
import { useUser } from 'hooks/SignUpContext';
import useAsync from 'hooks/useAsync';
import { MouseEvent, RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isProductFruitsReady } from 'react-product-fruits';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import AboutAppDialog from 'ui-kit/components/modal/AboutAppDialog';
import ContactUsDialog from 'ui-kit/components/modal/ContactUsDialog';
import { getCacheBustingVersion } from 'utilities/utilities';
import { FileDataContext, FileDataContextInterface } from '../../hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from '../../hooks/ProfileContext';
import { AccountMenu } from './AccountMenu/AccountMenu';
import { FirmSwitcher } from './FirmSwitcher/FirmSwitcher';

export enum CurrentView {
  Portal = 'portal',
  AdminUser = 'admin/user',
  Admin = 'admin',
  Public = 'public',
  AccountNotFound = 'accountNotFound',
}

// EULA Updated Date - Users must re-accept if they accepted before this date
const EULA_UPDATED_DATE = '2026-06-18T00:00:00';

interface GlobalHeaderProps {
  onViewSwitch?: () => void;
  currentView?: CurrentView | string;
  onPortalSwitch?: () => void;
  hideNav?: boolean;
  hasPermission?: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerWrapper: {
      flexShrink: 0,
      position: 'fixed',
      width: '100%',
      zIndex: 15,
    },
    signInBtn: {
      marginLeft: theme.spacing(4),
    },
    sectionDesktop: {
      display: 'flex',
    },
    accountButton: {
      alignItems: 'center',
      padding: 0,
      height: '64px',
      display: 'flex',
      minWidth: 'auto',
      '&:hover': {
        color: theme.palette.primary.contrastText,
      },
      '& .MuiTypography-root': {
        color: theme.palette.primary.contrastText,
        fontWeight: 500,
      },
    },
    accountButtonTxt: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '40px',
      border: `1px solid rgba(255, 255, 255, 0.23)`,
      textAlign: 'center',
      '& span': {
        marginTop: '4px',
      },
    },
    createNewFileBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
);

const GlobalHeader = ({ onViewSwitch, currentView, hideNav, hasPermission }: GlobalHeaderProps): JSX.Element => {
  const auth = useAuth();
  const classes = useStyles();
  const { signOut, getAccessToken, account, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, setUserFirm, profile, latestProfile, pfNotifications, setPfNotifications, reloadProfile } =
    profileCtx;
  const params: { firmId?: string; action?: string } = useParams();
  const { setUserId, setPreviousPage, previousPage, setLoginUserId } = useUser();
  const isEulaReacceptanceEnabled = useFeatureFlag(FeatureFlag.EULA_REACCEPTANCE_ENABLED, false);
  const isReloadingAfterEula = useRef(false);

  const {
    setOpenSaveModal,
    isFormDirty,
    firmAnchorEl,
    setFirmAnchorEl,
    setIsCreateDrawerClicked,
    openCreateDrawer,
    setOpenCreateDrawer,
  }: FileDataContextInterface = useContext(FileDataContext) ?? {};

  const isFirmMenuOpen = Boolean(firmAnchorEl);

  const defaultFirm = getDefaultFirm(profile?.firms);
  const [firm, setFirm] = useState<{ name: string; value: string }>(
    userFirm?.firmId
      ? { name: userFirm.name, value: userFirm.firmId }
      : { name: defaultFirm?.name ?? '', value: defaultFirm?.firmId ?? '' },
  );
  const claims = account?.idTokenClaims as any;
  const isEmployee = claims?.onpremADusername != null;
  const agentNetKnowledgeUrl = isEmployee ? getAgentNetKnowledgeUrl() + '?type=b2e' : getAgentNetKnowledgeUrl();
  const agentNetMarketingUrl = isEmployee ? getAgentNetMarketingUrl() + 'b2e' : getAgentNetMarketingUrl();
  const agentPrintProUrl = isEmployee
    ? 'https://agentprintpro.com/sso/initiate/fc550000-5dcb-0015-d363-08d864b6da16'
    : 'https://agency.myfirstam.com/Authentication/SignIn?_ref=8oj%2fhK0Qn4svaymJ43DtytcrjRgSiU%2f2ZXglgmp%2flT0%3d';
  const anteUrl = isEmployee
    ? `${getAnteUrl()}TitleEscrow//FileService/FileService.html?v1=${
        userFirm?.firmId
      }&v=${getCacheBustingVersion()}&isStaff=1`
    : `${getAnteUrl()}TitleEscrow//FileService/FileService.html?v1=${userFirm?.firmId}&v=${getCacheBustingVersion()}`;

  const isSupportEnv = anteUrl.toLowerCase().includes('support');
  const isSameUserName =
    isEmployee &&
    profile?.firstName?.toLowerCase() == claims?.given_name.toLowerCase() &&
    profile?.lastName?.toLowerCase() == claims?.family_name.toLowerCase();
  const isWorkAsUser = isSupportEnv && isEmployee && !isSameUserName;
  const workAsUsertxt = isWorkAsUser ? 'WORKING AS USER' : '';
  const resolveAnmUrl = () => {
    const anmRedirectUrl = `${window.location.protocol}//${window.location.host}/anm-redirect`;
    window.open(anmRedirectUrl, '_blank');
  };
  const allNavigationLinks: NavigationLink[] = [
    {
      category: CategoryType.OWNED,
      description: 'Forms, Bulletins, Education and UW Guidance',
      icon: <Knowledge height={32} width={32} />,
      text: 'AgentNet Knowledge',
      url: agentNetKnowledgeUrl,
    },
    {
      category: CategoryType.OWNED,
      description: 'Build Your Brand and Grow Your Business',
      icon: <AgentNetMarketing />,
      text: 'AgentNet Marketing',
      url: agentNetMarketingUrl,
      onClick: () => resolveAnmUrl(),
    },
    ...(userFirm?.isTitleEscrow
      ? [
          {
            category: CategoryType.OWNED,
            description: 'Title & Escrow for First American Transactions',
            icon: <AgentNetTitleEscrow />,
            text: 'AgentNet Production Essentials',
            url: anteUrl,
          },
        ]
      : []),
    {
      category: CategoryType.OTHER,
      description: 'Special Offers From Our Favorite Suppliers',
      icon: <Discownts height={32} width={32} />,
      text: 'discownts.com®',
      url: isEmployee
        ? 'https://www.discownts.com/Default.aspx'
        : `https://www.discownts.com/AXS_Landing.aspx?token=${
            profile?.azureObjectId ? encodeURIComponent(profile.azureObjectId.replace(/-/g, '')) : ''
          }`,
    },
    {
      category: CategoryType.OTHER,
      description: 'Title Rates & Recording Fee Estimates',
      icon: <Comprehensive height={32} width={32} />,
      text: 'First American Comprehensive Calculator',
      url: 'https://agency.facc.firstam.com/',
    },
    {
      category: CategoryType.OTHER,
      description: 'Master AgentNet® Integrations with Ease',
      icon: <SoftwareIntegrationHub />,
      text: 'Software Integration Hub',
      url: 'https://agency.myfirstam.com/ApplicationTabGUI/IntegrationTraining.html',
    },
  ];

  // ANS-6600: Filter App Launcher links based on Approved Attorney role permissions
  // Block: AgentNet Marketing, Software Integration Hub, AgentNet Production Essentials
  // Allow: AgentNet Knowledge, discownts.com®, Rate Calculator
  const navigationLinks = allNavigationLinks.filter((link) => {
    // Check if we should apply Approved Attorney restrictions
    const shouldApplyRestrictions = hasPermission?.applyApprovedAttorneyRestrictions ?? false;

    if (shouldApplyRestrictions) {
      // Block specific links for Approved Attorney (per ANS-6600 acceptance criteria #7)
      if (link.text === 'AgentNet Marketing') return false;
      if (link.text === 'Software Integration Hub') return false;
      if (link.text === 'AgentNet Production Essentials') return false;
    }

    return true;
  });

  const { isStaffAdmin, isFSC } = useMemo(() => {
    let isStaffAdminUser = false;
    let isFSCUser = false;
    if (profile) {
      isStaffAdminUser = profile?.roleId === RoleId.STAFF_ADMIN;
      isFSCUser = hasPermission?.accessOrderManagement;
    }
    return {
      isStaffAdmin: isStaffAdminUser,
      isFSC: isFSCUser,
    };
  }, [profile]);

  useEffect(() => {
    // Wait for both profile and feature flag to be ready before checking EULA
    if (!profile || !isEulaReacceptanceEnabled?.ready) {
      return;
    }

    // Skip EULA check if we're currently reloading after EULA acceptance
    if (isReloadingAfterEula.current) {
      return;
    }

    // Check if user needs to accept EULA
    let needsEulaAcceptance = false;

    if (isEulaReacceptanceEnabled?.value) {
      // New logic: Check if user has never accepted EULA
      if (profile?.eulaAcceptedDate == null) {
        needsEulaAcceptance = true;
      } else {
        // Check if user's accepted date is before the updated EULA date
        const userAcceptedDate = new Date(profile.eulaAcceptedDate);
        const eulaUpdatedDate = new Date(EULA_UPDATED_DATE);

        if (userAcceptedDate < eulaUpdatedDate) {
          needsEulaAcceptance = true;
        }
      }
    } else {
      // Old logic: Only check if user has never accepted EULA
      if (profile?.eulaAcceptedDate == null) {
        needsEulaAcceptance = true;
      }
    }

    if (
      profile?.roleTypeId === 100 &&
      needsEulaAcceptance &&
      !(previousPage && previousPage == '/authorization/eula')
    ) {
      setUserId(profile?.userId?.toString() ?? null);
      setLoginUserId(profile?.login?.toString() ?? null);
      setPreviousPage('/files');
      history.push('/authorization/eula');
    }
  }, [profile, isEulaReacceptanceEnabled?.ready, isEulaReacceptanceEnabled?.value]);

  useEffect(() => {
    const handlePostEulaReload = async () => {
      if (profile && previousPage && previousPage == '/authorization/eula') {
        // Set flag to skip EULA check during reload
        isReloadingAfterEula.current = true;

        try {
          // Reload profile to get updated EULA acceptance date
          // Wait for the reload to complete before proceeding
          if (reloadProfile) {
            await reloadProfile();
          }
        } catch (error) {
          // Silently handle error - cleanup will still occur in finally block
        } finally {
          // Clear the flag and previousPage after profile reload completes
          // This ensures we have the updated EULA acceptance date before checking again
          isReloadingAfterEula.current = false;
          setPreviousPage(null);
        }
      }
    };

    handlePostEulaReload();
  }, []);

  const previousFirm = useRef(firm).current;
  const history = useHistory();
  const location = useLocation();
  const orderManagementUrl = '/order-management';
  const isOrderManagementPage = location.pathname.startsWith(orderManagementUrl);

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  function getEmail() {
    let email = '';
    if (latestProfile) {
      email = latestProfile?.EmailAddress ? latestProfile?.EmailAddress : '';
    } else if (profile) {
      email = profile?.emailAddress ? profile.emailAddress : '';
    } else if (auth.isAuthenticated() && auth.account?.idTokenClaims) {
      email = auth.account?.idTokenClaims?.Email ? (auth.account?.idTokenClaims?.Email as string) : '';
    }
    return email;
  }
  function getFullName() {
    let fullName = '';
    if (latestProfile) {
      fullName = latestProfile?.FirstName ? latestProfile?.FirstName + ' ' : '';
      fullName += latestProfile?.LastName ? latestProfile?.LastName : '';
    } else if (profile) {
      fullName = profile?.firstName ? profile?.firstName + ' ' : '';
      fullName += profile?.lastName ? profile?.lastName : '';
    } else if (auth.isAuthenticated() && auth.account?.idTokenClaims) {
      fullName = auth.account?.idTokenClaims?.FirstName ? auth.account?.idTokenClaims?.FirstName + ' ' : '';
      fullName += auth.account?.idTokenClaims?.LastName ? auth.account?.idTokenClaims?.LastName : '';
    }
    return fullName;
  }

  function getNameTag() {
    let fname = 'U';
    let lname = '';
    if (latestProfile) {
      fname = latestProfile?.FirstName ? latestProfile.FirstName[0].toUpperCase() : '';
      lname = latestProfile?.LastName ? latestProfile.LastName[0].toUpperCase() : '';
    } else if (profile) {
      fname = profile?.firstName ? profile.firstName[0].toUpperCase() : '';
      lname = profile?.lastName ? profile.lastName[0].toUpperCase() : '';
    } else if (auth.isAuthenticated() && auth.account?.idTokenClaims) {
      fname = auth.account?.idTokenClaims?.FirstName
        ? (auth.account.idTokenClaims.FirstName as string)[0].toUpperCase()
        : '';
      lname = auth.account?.idTokenClaims?.LastName
        ? (auth.account.idTokenClaims.LastName as string)[0].toUpperCase()
        : '';
    }
    return `${fname}${lname}`;
  }

  useEffect(() => {
    if (!isFSC && isOrderManagementPage && profile) {
      history.push('/order-unauthorized-access');
    }
  }, [isFSC, isOrderManagementPage, profile]);

  useEffect(() => {
    setFirmAnchorEl(null);
  }, [location.pathname]);

  function outsideClickPopper(ref: RefObject<HTMLDivElement>) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setFirmAnchorEl(null);
          if (firm.value === '') {
            setFirm(previousFirm);
          }
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  const popperRef = useRef(null);
  outsideClickPopper(popperRef);

  const [aboutModal, setAboutModal] = useState<boolean>(false);
  const [contactUsModal, setContactUsModal] = useState<boolean>(false);

  useEffect(() => {
    window.productFruitsReady = () => {
      window.productFruits?.api?.announcementsV2.listen('newsfeed-unread-count-changed', (data: any) => {
        setPfNotifications && setPfNotifications(data.count);
      });
      attachNewsFeed();
    };
    attachNewsFeed();
  });

  const attachNewsFeed = () => {
    if (isProductFruitsReady()) {
      const customLauncher = document.getElementById('notificationIcon');
      if (customLauncher) {
        window.productFruits?.api?.announcementsV2.attachNewsWidgetToElement(customLauncher);
      }
    }
  };

  const handleFirmChange = (newValue: string | undefined, logout = false, firmDetailsResults?: any) => {
    let matchedFirmData;

    if (firmDetailsResults?.firmId === newValue && (profile?.roleId === 3 || profile?.roleId === 5)) {
      matchedFirmData = firmDetailsResults;
    } else {
      matchedFirmData = profile?.firms?.find((firm: Firm) => String(firm.firmId) === String(newValue));
    }
    if (matchedFirmData) {
      setFirm({ name: matchedFirmData.name, value: matchedFirmData.firmId });
      window.localStorage.setItem('userFirm', JSON.stringify(matchedFirmData));
      setUserFirm && setUserFirm(matchedFirmData);
      setFirmAnchorEl(null);
      if (location.pathname.indexOf('/files') > -1) history.push('/files');
      if (location.pathname.includes('/invoice-summary') || params?.action === 'pay')
        history.push('/accounting/remittance/pay');
      if (location.pathname.includes('/accounting/service-order-invoices/view-invoices')) {
        history.push('/accounting/service-order-invoices/view-invoices');
      }
    } else if (logout) {
      signOut().then();
      sessionStorage.clear();
      localStorage.clear();
    }
  };

  const getFirmDetail = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    return await getFirmDetails(token, params?.firmId ?? '');
  };

  const {
    execute: executeFirmDetails,
    status: executeFirmDetailsStatus,
    value: firmDetailsResults,
  } = useAsync(getFirmDetail, false);

  useEffect(() => {
    if (params?.firmId && params?.firmId !== 'invoice-summary') {
      if (profile?.roleId === 3 || profile?.roleId === 5) {
        executeFirmDetails().then();
      } else if (profile?.firms) {
        handleFirmChange(params?.firmId, true);
      }
    }
  }, [params?.firmId, profile?.firms]);

  useEffect(() => {
    if (firmDetailsResults) {
      handleFirmChange(params?.firmId, true, firmDetailsResults);
    }
  }, [firmDetailsResults]);

  useEffect(() => {
    if (executeFirmDetailsStatus === 'error') {
      handleFirmChange('', true);
    }
  }, [executeFirmDetailsStatus]);

  const isDashboardEnabled = true; //isEnableAllFeatures();

  const getAccountingUrl = (): string => {
    if (hasPermission?.orderManagement) return '/accounting/service-order-invoices/view-invoices';
    if (hasPermission?.remit) return '/accounting/remittance/report';
    if (hasPermission?.pay) return '/accounting/remittance/pay';

    return '/accounting/service-order-invoices/view-invoices';
  };

  const getServicesUrl = (): string => {
    // Priority order: specific service permissions first
    if (hasPermission?.orderManagement) return '/services/sdn-search';
    if (hasPermission?.backtitle) return '/services/back-title-search';
    if (hasPermission?.canRunPolicyUpload) return '/services/policy-upload/upload';

    // ANS-6600: For users without specific service permissions, default to SDN Search (e.g., Approved Attorney)
    // This ensures restricted users have a valid landing page
    return '/services/sdn-search';
  };

  const filesUrl = hasPermission?.orderManagement ? '/searchFirms' : '/files';
  const accountingUrl = getAccountingUrl();
  const servicesUrl = getServicesUrl();
  const dashboardUrl = '/dashboards/service-orders';
  const reportsUrl = '/reporting';
  const adminUrl = '/admin/user/profile';
  const orderManagementAdminUrl = '/admin/users/manage';
  const showAgentNetMenu =
    hasPermission?.hasFirmId && (!location.pathname.startsWith(orderManagementUrl) || hasPermission?.crsUser);

  const topNonOrderManagementLinks: BaseLink[] = [
    ...(hasPermission?.filesList ? [{ text: 'Files', testId: 'TopNavFiles', url: filesUrl }] : []),
    ...(hasPermission?.isGAFundUser || !hasPermission?.accounting
      ? []
      : [{ text: 'Accounting', testId: 'TopNavAccounting', url: accountingUrl }]),
    { text: 'Services', testId: 'TopNavServices', url: servicesUrl },
    ...(isDashboardEnabled && hasPermission?.dashboard
      ? [{ text: 'Dashboards', testId: 'TopNavDashboards', url: dashboardUrl }]
      : []),
    ...(hasPermission?.isGAFundUser || !hasPermission?.reports
      ? []
      : [{ text: 'Reports', testId: 'TopNavReports', url: reportsUrl }]),
    { text: 'Admin', testId: 'TopNavAdmin', url: adminUrl },
  ];

  const topOrderManagementLinks: BaseLink[] = [
    { text: 'Files', testId: 'TopNavFiles', url: filesUrl },
    { text: 'Order Management', testId: 'TopNavOrderManagement', url: orderManagementUrl },
    ...(showAgentNetMenu
      ? [
          { text: 'Accounting', testId: 'TopNavAccounting', url: accountingUrl },
          { text: 'Services', testId: 'TopNavServices', url: servicesUrl },
          ...(isDashboardEnabled ? [{ text: 'Dashboards', testId: 'TopNavDashboards', url: dashboardUrl }] : []),
          { text: 'Reports', testId: 'TopNavReports', url: reportsUrl },
          { text: 'Admin', testId: 'TopNavAdmin', url: adminUrl },
        ]
      : []),
  ];

  const topOrderManagementAdminLinks: BaseLink[] = [
    { text: 'Files', testId: 'TopNavFiles', url: filesUrl },
    { text: 'Order Management', testId: 'TopNavOrderManagement', url: orderManagementUrl },
    ...(showAgentNetMenu
      ? [
          { text: 'Accounting', testId: 'TopNavAccounting', url: accountingUrl },
          { text: 'Services', testId: 'TopNavServices', url: servicesUrl },
          ...(isDashboardEnabled ? [{ text: 'Dashboards', testId: 'TopNavDashboards', url: dashboardUrl }] : []),
          { text: 'Reports', testId: 'TopNavReports', url: reportsUrl },
          { text: 'Admin', testId: 'TopNavAdmin', url: adminUrl },
        ]
      : [{ text: 'Admin', testId: 'TopNavOMAdmin', url: orderManagementAdminUrl }]),
  ];

  const getTopNavLinks = (): BaseLink[] => {
    if (!hideNav && !isStaffAdmin && !isFSC) {
      return topNonOrderManagementLinks;
    }
    if (isFSC) {
      if (hasPermission?.orderManagementAdmin) {
        return topOrderManagementAdminLinks;
      }
      return topOrderManagementLinks;
    }
    return [];
  };

  const getButtonDataQa = (): string => {
    if (!hideNav && !isStaffAdmin && !hasPermission?.orderManagement) {
      return 'GlobalNavCreateNewFile';
    }
    if (isFSC) {
      return 'GlobalNavCreateNewOrder';
    }
    return '';
  };

  const getButtonText = (): string => {
    if (isFSC && isOrderManagementPage) {
      return 'Create New Order';
    }
    return 'Create New File';
  };

  const shouldRenderTopNav = (): boolean => {
    return (!hideNav && !isStaffAdmin) || isFSC;
  };

  const noBehalfOfUser =
    profile?.roleId === 2 || profile?.roleId === 13 || (hasPermission?.orderManagementAdmin && !isOrderManagementPage);

  const [openCreateFileDrawer, setOpenCreateFileDrawer] = useState<boolean>(false);

  useEffect(() => {
    if (openCreateDrawer) {
      // based on file context open the create drawer
      setOpenCreateFileDrawer(true);
      setOpenCreateDrawer?.(false);
    }
  }, [openCreateDrawer]);

  const isOrderManagementDashboard = location.pathname.startsWith(orderManagementUrl) && !location.state;

  return (
    <>
      {location.pathname.includes('/Authentication/ForgotPassword') ||
      location.pathname.includes('/Authentication/ForgotUserID') ? null : (
        // TODO: Refactor app css to remove flex on container
        <div className={classes.headerWrapper}>
          <AppHeader
            links={
              location.pathname.includes('/contact-us') || currentView === CurrentView.AccountNotFound
                ? undefined
                : navigationLinks
            }
            appBar={
              <AppBar
                logo={
                  <>
                    {currentView === CurrentView.AccountNotFound ? (
                      <AgentNetServices width={149} height={36} />
                    ) : (
                      <Link to="/">
                        <AgentNetServices width={149} height={36} />
                      </Link>
                    )}
                  </>
                }
                componentRight={
                  <div className={classes.sectionDesktop}>
                    {currentView !== CurrentView.AccountNotFound && (
                      <>
                        <div className="custom-pf">{workAsUsertxt}</div>
                        {currentView === CurrentView.Public && (
                          <>
                            {window.location.pathname === '/account-registration' ? (
                              <AgentNetButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  window.location.href = '/contact-us';
                                }}
                              >
                                Contact Us
                              </AgentNetButton>
                            ) : (
                              <AgentNetButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  window.location.href = '/account-registration';
                                }}
                              >
                                Request Access
                              </AgentNetButton>
                            )}
                            <AgentNetButton
                              className={classes.signInBtn}
                              color="primary"
                              size="medium"
                              variant="contained"
                              onClick={() => {
                                logout().then();
                              }}
                            >
                              Sign In
                            </AgentNetButton>
                          </>
                        )}
                        {currentView !== CurrentView.Public && !isOrderManagementDashboard && (
                          <FirmSwitcher
                            firm={firm}
                            firmAnchorEl={firmAnchorEl}
                            isFirmMenuOpen={isFirmMenuOpen}
                            pfNotifications={pfNotifications}
                            popperRef={popperRef}
                            profile={profile}
                            setFirm={setFirm}
                            setFirmAnchorEl={setFirmAnchorEl}
                            userFirm={userFirm}
                            handleFirmChange={handleFirmChange}
                            handleMenuClose={handleMenuClose}
                          />
                        )}

                        {currentView !== CurrentView.Public && (
                          <Button
                            aria-label="account of current user"
                            aria-controls={'account-menu'}
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            color="inherit"
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            variant="text"
                            size="large"
                            className={classes.accountButton}
                            data-testid="HeaderUserAccount"
                          >
                            <div className={classes.accountButtonTxt}>
                              <Typography variant="h2">{getNameTag()} </Typography>
                            </div>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                }
              />
            }
          />

          <AccountMenu
            anchorEl={anchorEl}
            email={getEmail()}
            isStaffAdmin={isStaffAdmin}
            name={getFullName()}
            profile={profile}
            userFirm={userFirm}
            handleMenuClose={handleMenuClose}
            onViewSwitch={onViewSwitch}
            setAboutModal={setAboutModal}
            setContactUsModal={setContactUsModal}
          />

          {shouldRenderTopNav() && (
            <TopNav
              links={getTopNavLinks()}
              componentRight={
                <>
                  {noBehalfOfUser || currentView === CurrentView.Admin ? null : (
                    <div className={classes.createNewFileBtn}>
                      <AgentNetButton
                        color="primary"
                        size="small"
                        variant="contained"
                        data-testid={getButtonDataQa()}
                        disabled={false}
                        onClick={() => {
                          // If the Edit File form has unsaved changes, open the modal
                          if (isFormDirty) {
                            setOpenSaveModal && setOpenSaveModal(true);
                            setIsCreateDrawerClicked && setIsCreateDrawerClicked(true);
                          }
                          // otherwise, just open the drawer
                          else {
                            setOpenCreateFileDrawer(true);
                            setFirmAnchorEl && setFirmAnchorEl(null);
                          }
                        }}
                        fullWidth
                        plusIcon
                      >
                        {getButtonText()}
                      </AgentNetButton>
                    </div>
                  )}
                </>
              }
            />
          )}
        </div>
      )}
      <AboutAppDialog open={aboutModal} onClose={() => setAboutModal(false)} />
      <ContactUsDialog open={contactUsModal} onClose={() => setContactUsModal(false)} />
      {isFSC && isOrderManagementPage ? (
        <OrderCreateContainer {...{ openDrawer: openCreateFileDrawer, setOpenDrawer: setOpenCreateFileDrawer }} />
      ) : (
        <FileCreateProvider>
          <FileCreateContainer {...{ openDrawer: openCreateFileDrawer, setOpenDrawer: setOpenCreateFileDrawer }} />
        </FileCreateProvider>
      )}
    </>
  );
};

export default GlobalHeader;
